// Visual primitives for the report shell — Pill, Trend, buttons.
// Pure presentational; no business logic.
import React from 'react'
import { theme as defaultTheme, T } from './tokens'

/**
 * Pill — small status / counter badge.
 * kind: 'good' | 'warn' | 'bad' | 'info' | 'neutral'
 */
export function Pill({ kind = 'neutral', small = false, children, theme = defaultTheme, style }) {
  const c = {
    good:    { bg: theme.goodSub, fg: theme.good, br: `${theme.good}33` },
    warn:    { bg: theme.warnSub, fg: theme.warn, br: `${theme.warn}33` },
    bad:     { bg: theme.badSub,  fg: theme.bad,  br: `${theme.bad}33` },
    info:    { bg: `${theme.accent}15`, fg: theme.accent, br: `${theme.accent}33` },
    neutral: { bg: theme.bgSub, fg: theme.textSub, br: theme.border },
  }[kind] || { bg: theme.bgSub, fg: theme.textSub, br: theme.border }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: small ? '2px 8px' : '3px 10px',
      borderRadius: 999,
      background: c.bg,
      color: c.fg,
      border: `1px solid ${c.br}`,
      fontSize: small ? 10.5 : 11.5,
      fontWeight: 600,
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
      fontFamily: T.fontUI,
      ...style,
    }}>
      {children}
    </span>
  )
}

/**
 * Trend — arrow + percentage. Uses semantic colors.
 * Pass `value` as signed percent; use `inverse` if down is good (e.g. errors).
 */
export function Trend({ value, theme = defaultTheme, size = 12, inverse = false }) {
  if (value == null || Number.isNaN(value)) return null
  const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'flat'
  const goodDir = inverse ? 'down' : 'up'
  const c = dir === goodDir ? theme.good
          : dir === 'flat' ? theme.textDim
          : theme.bad
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→'
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 2,
      color: c, fontSize: size, fontWeight: 600,
      fontFamily: T.fontMono,
    }}>
      <span style={{ fontSize: size + 2, lineHeight: 1 }}>{arrow}</span>
      {Math.abs(value).toFixed(1).replace('.', ',')}%
    </span>
  )
}

export const btnPrimary = (theme = defaultTheme) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 7,
  background: theme.accent, color: '#fff',
  border: 'none',
  fontSize: 12.5, fontWeight: 600,
  cursor: 'pointer',
  fontFamily: T.fontUI,
  whiteSpace: 'nowrap',
})

export const btnSecondary = (theme = defaultTheme) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 7,
  background: theme.surface, color: theme.text,
  border: `1px solid ${theme.border}`,
  fontSize: 12.5, fontWeight: 500,
  cursor: 'pointer',
  fontFamily: T.fontUI,
  whiteSpace: 'nowrap',
})

export const btnGhost = (theme = defaultTheme) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 7,
  background: 'transparent', color: theme.textSub,
  border: `1px solid ${theme.border}`,
  fontSize: 12, fontWeight: 500,
  cursor: 'pointer',
  fontFamily: T.fontUI,
})
